import PropTypes from "prop-types";
import { useFormContext, useWatch } from "react-hook-form";

import { FieldGroup } from "@/components/ui/field";
import {
  FormInput,
  FormRecordPicker,
  FormSelect,
  SectionCard,
} from "components/BaseForm";
import { formatUserName, isUserActive } from "helpers/user";
import { RECIPIENT_TYPE_OPTIONS } from "routes/revenue_opportunity/form/constants";
import LocationSection from "routes/revenue_opportunity/form/sections/LocationSection";
import RecipientService from "services/Recipient";
import UserService from "services/User";

const withSearch = (search, filters = {}) =>
  search ? { ...filters, search_query: search } : filters;

const DetailsSection = ({ displays }) => {
  const { setValue } = useFormContext();
  const [assigneeId, recipientType, campaignId] = useWatch({
    name: ["assignee_id", "recipient_type", "campaign_id"],
  });

  // Deactivated users are not offered, except the one already assigned.
  const fetchAssignees = async search => {
    const users = await UserService.getUsersForFilters(withSearch(search));
    return users
      .filter(
        user => user.id > 0 && (user.id === assigneeId || isUserActive(user))
      )
      .map(user => ({ ...user, name: formatUserName(user.name, user.status) }));
  };

  const fetchRecipients = search =>
    RecipientService.getForFilters(
      withSearch(search, {
        types: recipientType,
        ...(campaignId && { campaign_id: campaignId }),
      })
    );

  return (
    <SectionCard title="Details">
      <LocationSection />
      <FieldGroup className="tw:grid tw:grid-cols-1 tw:gap-5 tw:md:grid-cols-2">
        <FormRecordPicker
          name="assignee_id"
          label="Assignee"
          placeholder="Pick an Assignee"
          fetchOptions={fetchAssignees}
          optionsKey="ro-assignee"
          display={displays.assignee}
        />
        <FormInput name="billing_to" label="Billing To" />
        <FormSelect
          name="recipient_type"
          label="Recipient Type"
          placeholder="Pick a Recipient Type"
          options={RECIPIENT_TYPE_OPTIONS}
          clearable
          onValueChange={() =>
            setValue("recipient_id", null, { shouldDirty: true })
          }
        />
        <FormRecordPicker
          name="recipient_id"
          label="Recipient Name"
          placeholder="Pick a Recipient"
          fetchOptions={fetchRecipients}
          optionsKey={`ro-recipient-${recipientType}-${campaignId}`}
          display={displays.recipient}
          readOnly={!recipientType}
        />
      </FieldGroup>
    </SectionCard>
  );
};

const displayShape = PropTypes.shape({
  id: PropTypes.number,
  label: PropTypes.string,
});

DetailsSection.propTypes = {
  displays: PropTypes.shape({
    assignee: displayShape,
    recipient: displayShape,
  }).isRequired,
};

export default DetailsSection;
